import {Trans} from '@lingui/macro';
import React from 'react';

import {styled} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import requiredTaggingIllustration from '../../../../../assets/images/requiredTagging.png';
import {AccordionSectionData} from '../../../../../components/common/accordion/accordionSection';
import {RuleEditorSidebarLearnMore} from '../../ruleEditorSidebarLearnMore';
import {buildRuleEditorFaqSections, RuleEditorRecipeFaq} from '../helpers/ruleEditorRecipeFaq';

/*
 * Props.
 */

interface RequiredTaggingEditorSidebarProps {}

/*
 * Styles.
 */

const StyledIllustrationImg = styled.img`
  border-radius: 16px;
  width: 100%;
  margin-top: 13px;
`;

/*
 * Component.
 */

export const RequiredTaggingEditorSidebar: React.FC<RequiredTaggingEditorSidebarProps> = (props) => (
  <RuleEditorRecipeFaq sections={getRequiredTaggingFaqSections()} />
);

/*
 * Helpers.
 */

function getRequiredTaggingFaqSections(): ReadonlyArray<AccordionSectionData> {
  const sections = [
    {
      title: (
        <Trans id="required-tagging-editor-sidebar-item-1-title">
          How will it work for my team in their inboxes?
        </Trans>
      ),
      content: (
        <>
          <Trans id="required-tagging-editor-sidebar-item-1-content">
            <p>In app, your team will have direct access to the required tag list from the tag dropdown.</p>
            <p>They will also get a prompt to tag if they attempt to archive or move before tagging.</p>
          </Trans>
          <RuleEditorSidebarLearnMore
            onClick={() => openInBrowser('https://help.front.com/t/60hgg0p/required-tagging')}
          />
          <StyledIllustrationImg src={requiredTaggingIllustration} alt="Required Tagging illustration" />
        </>
      ),
    },
    {
      title: (
        <Trans id="required-tagging-editor-sidebar-item-2-title">
          What happens if a rule archives a message without a tag?
        </Trans>
      ),
      content: (
        <>
          <Trans id="required-tagging-editor-sidebar-item-2-content">
            <p>Rules are not impacted by required tagging. No tag will be added in this case.</p>
          </Trans>
          <RuleEditorSidebarLearnMore
            onClick={() => openInBrowser('https://help.front.com/t/60hgg0p/required-tagging')}
          />
        </>
      ),
    },
    {
      title: (
        <Trans id="required-tagging-editor-sidebar-item-3-title">
          How do I create a required tagging rule?
        </Trans>
      ),
      content: (
        <>
          <Trans id="required-tagging-editor-sidebar-item-3-content">
            <p>
              First, add conditions so that the tags will be required only on the specific conversations you
              need.
            </p>
            <p>Then, select and name a list of required tags.</p>
            <p>
              <b>Example:</b> you need your team to tag each support-related inbound with a region tag. In
              this rule, you select the Support inbox under “If...”. Under “Then...”, you select region tags,
              and you name this tag list “Regions”.
            </p>
          </Trans>
          <RuleEditorSidebarLearnMore
            onClick={() => openInBrowser('https://help.front.com/t/60hgg0p/required-tagging')}
          />
        </>
      ),
    },

    {
      title: (
        <Trans id="required-tagging-editor-sidebar-item-4-title">
          Why do I need to name the list of tags I select?
        </Trans>
      ),
      content: (
        <>
          <Trans id="required-tagging-editor-sidebar-item-4-content">
            <p>This name will be how your teammates will access the tag list when applying tags.</p>
            <p>
              <b>Example:</b> you need all inbounds to have a client tag. In this rule, select all of your
              client tags, and then name that list “Clients”. Your team will see “Clients” at the top of the
              tags dropdown.
            </p>
          </Trans>
          <RuleEditorSidebarLearnMore
            onClick={() => openInBrowser('https://help.front.com/t/60hgg0p/required-tagging')}
          />
        </>
      ),
    },
  ];

  return buildRuleEditorFaqSections(sections);
}
